import type { ScrollRevealVariant } from "../../hooks/useScrollReveal"

export const SCROLL_REVEAL_DURATION_MS = 700
export const SCROLL_REVEAL_STAGGER_MS = 80

/** Reveal tuning for infinite-scroll review lists (batch-local stagger, no cumulative index delay). */
export const REVIEWS_PAGE_SCROLL_REVEAL_CLASSES =
  "duration-500 ease-out motion-reduce:transition-none"
export const REVIEWS_PAGE_STAGGER_MS = 33
export const REVIEWS_PAGE_BATCH_SIZE = 10
/** First N reviews of each infinite-scroll page render without reveal animation. */
export const REVIEWS_PAGE_INSTANT_REVEAL_COUNT = 5

export function shouldInstantRevealReview(
  index: number,
  isOnReviewsPage: boolean,
  totalLoaded: number
): boolean {
  if (!isOnReviewsPage) return false

  const batchIndex = Math.floor(index / REVIEWS_PAGE_BATCH_SIZE)
  const indexInBatch = index % REVIEWS_PAGE_BATCH_SIZE

  // First 5 of each batch loaded via infinite scroll (page 2+).
  if (batchIndex >= 1 && indexInBatch < REVIEWS_PAGE_INSTANT_REVEAL_COUNT) {
    return true
  }

  // Last 5 of a batch once the next page has loaded — avoids re-animating the page before.
  if (
    indexInBatch >= REVIEWS_PAGE_INSTANT_REVEAL_COUNT &&
    totalLoaded > (batchIndex + 1) * REVIEWS_PAGE_BATCH_SIZE
  ) {
    return true
  }

  return false
}

export const scrollRevealHiddenClasses: Record<Exclude<ScrollRevealVariant, "none">, string> = {
  fadeUp: "translate-y-5 opacity-0",
  fadeIn: "opacity-0",
  scaleIn: "scale-[0.98] opacity-0"
}

export const scrollRevealVisibleClasses: Record<Exclude<ScrollRevealVariant, "none">, string> = {
  fadeUp: "translate-y-0 opacity-100",
  fadeIn: "opacity-100",
  scaleIn: "scale-100 opacity-100"
}

export function getScrollRevealTransitionClass(reducedMotion: boolean): string {
  if (reducedMotion) return ""
  return "transition-all duration-700 ease-out motion-reduce:transition-none motion-reduce:transform-none motion-reduce:opacity-100"
}

/** Hover motion for card images (theme-agnostic). */
export const CARD_IMAGE_HOVER_CLASSES =
  "transition-[transform,filter] duration-700 ease-out group-hover:scale-105 motion-reduce:transform-none"

export const CARD_IMAGE_OVERLAY_CLASSES =
  "pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100 motion-reduce:opacity-0"

export const CARD_ROW_HOVER_CLASSES =
  "transition-[border-color,background-color,transform,opacity] duration-500 ease-out hover:border-accent/40 motion-reduce:transition-none"
